import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Car } from 'lucide-react';
import { getRecentMileage } from '../lib/api';

/**
 * MileageRecentEntries
 *
 * Displays up to `limit` recent unique mileage routes (from+to pairs) fetched
 * from GET /api/expenses/mileage/recent. Clicking a card fires onSelect(entry)
 * so the parent form can auto-fill its fields.
 *
 * Renders nothing if: still loading, fetch failed, or the user has no history.
 *
 * Props:
 *   onSelect  {function(entry)}  — called with the full API entry object
 *   limit     {number}           — max entries to fetch (default 3)
 */
export default function MileageRecentEntries({ onSelect, limit = 3 }) {
    const { t } = useTranslation();
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(false);

        getRecentMileage(limit)
            .then((res) => {
                if (!cancelled) {
                    setEntries(res.data || []);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setError(true);
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => { cancelled = true; };
    }, [limit]);

    // Render nothing while loading or on error or when there is no history
    if (loading || error || entries.length === 0) return null;

    return (
        <div className="space-y-2">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">
                {t('expenses.mileage.recent_routes')}
            </p>
            <div className="flex flex-wrap gap-2">
                {entries.map((entry, i) => (
                    <button
                        key={i}
                        type="button"
                        onClick={() => onSelect(entry)}
                        aria-label={`Use route ${entry.from_location} to ${entry.to_location}, ${entry.distance_mil} mil, last used ${entry.last_used}`}
                        className="w-full sm:w-auto flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2.5 text-left text-sm hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors min-h-[44px]"
                    >
                        <Car className="w-4 h-4 shrink-0 text-muted-foreground" />
                        <span className="flex flex-col min-w-0">
                            <span className="font-medium truncate">
                                {entry.from_location} &rarr; {entry.to_location}
                            </span>
                            <span className="text-xs text-muted-foreground">
                                {entry.distance_mil} mil
                                {entry.round_trip ? ' · return' : ''}
                                {' · '}{entry.last_used}
                            </span>
                        </span>
                    </button>
                ))}
            </div>
        </div>
    );
}
